import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";

/**
 * Shows a golden welcome banner when the user lands on /dashboard?payment=success
 * (after a successful Stripe checkout). Dismissible + auto-dismiss in 10s.
 * Removes the query param via history.replaceState so refresh hides it.
 */
const PLAN_LABEL = {
  free: "Gratuito",
  personal: "Personal",
  personal_annual: "Personal Anual",
  lifetime: "De por Vida",
  founders: "Fundador",
};

export default function PaymentSuccessBanner() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const paymentStatus = params.get("payment");
  const [visible, setVisible] = useState(paymentStatus === "success" || paymentStatus === "cancelled");

  useEffect(() => {
    if (!visible) return;
    // Auto-dismiss after 10s
    const id = setTimeout(() => {
      setVisible(false);
      cleanUrl();
    }, 10000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const cleanUrl = () => {
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete("payment");
      u.searchParams.delete("session_id");
      window.history.replaceState({}, "", u.pathname + (u.search ? `?${u.searchParams.toString()}` : ""));
    } catch {
      // ignore
    }
  };

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    cleanUrl();
  };

  if (paymentStatus === "cancelled") {
    return (
      <div
        className="mb-6 glass-card rounded-md border border-faded/40 px-5 py-3 flex items-center justify-between gap-4"
        data-testid="payment-cancelled-banner"
      >
        <p className="text-sm text-muted">{t("payment_cancelled_banner")}</p>
        <button
          onClick={dismiss}
          aria-label={t("payment_dismiss")}
          className="text-faded hover:text-gold-light p-1"
          data-testid="payment-banner-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const plan = user?.plan || "premium";
  const isFounder = plan === "founders";
  const message = isFounder
    ? t("payment_success_founder").replace("{N}", user?.founder_number || "—")
    : t("payment_success_banner").replace("{PLAN}", PLAN_LABEL[plan] || plan);

  return (
    <div
      className="mb-6 rounded-md border-2 border-gold gold-glow px-5 py-4 flex items-center justify-between gap-4"
      style={{
        background: "linear-gradient(135deg, rgba(201,163,106,0.12), rgba(201,163,106,0.02))",
      }}
      data-testid="payment-success-banner"
    >
      <p className="text-sm sm:text-base text-gold-light font-medium">{message}</p>
      <button
        onClick={dismiss}
        aria-label={t("payment_dismiss")}
        className="text-gold/70 hover:text-gold p-1 shrink-0"
        data-testid="payment-banner-close"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
